import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { addToCart } from './services/cartService';

const ProductDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImg, setCurrentImg] = useState(0);
  const [enlarge, setEnlarge] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError('');
      try {
        const docRef = doc(db, 'products', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().status !== 'disable') {
          setProduct({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError(t('productDetails.productNotFound'));
        }
      } catch (err) {
        setError(t('productDetails.loadFailed'));
      }
      setLoading(false);
    };
    fetchProduct();
  }, [id, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-gray-500 text-xl">{t('productDetails.loadingProduct')}</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-red-500 text-xl">{error || t('productDetails.productNotFound')}</div>
      </div>
    );
  }

  const categoryTranslationKeys = {
    'Welding Machines': 'products.weldingMachines',
    Accessories: 'products.accessories',
    'Power Tools': 'products.powerTools',
    Generator: 'products.generator',
    'Air Compressors': 'products.airCompressors',
    'Home Improvements': 'products.homeImprovements',
    Others: 'products.promotions',
    Product: 'products.title',
    'Machinery Service Consultant': 'products.machineryService'
  };

  const getProductLabel = (label) => {
    const translationKey = categoryTranslationKeys[label];
    return translationKey ? t(translationKey) : label;
  };

  // Helper function to format price with RM and 2 decimal places
  const formatPrice = (price) => {
    if (!price) return '';
    // Remove any existing RM prefix and clean the price
    const cleanPrice = price.replace(/^RM\s*/, '').replace(/[^\d.]/g, '');
    const numPrice = parseFloat(cleanPrice);
    if (isNaN(numPrice)) return price; // Return original if can't parse
    return `RM ${numPrice.toFixed(2)}`;
  };

  // Helper function to get all images in correct order
  const getAllImages = () => {
    const images = [];
    if (product.image) {
      images.push(product.image); // Introduction image first
    }
    if (product.additionalImages && product.additionalImages.length > 0) {
      images.push(...product.additionalImages); // Additional images follow
    }
    return images;
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setQuantity(Math.max(1, value));
  };

  const decreaseQuantity = () => {
    setQuantity((prev) => Math.max(1, prev - 1));
  };

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const productLabel = getProductLabel((product.subCategory || product.category || 'Product').toString());

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1 flex flex-col py-4 md:py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-10">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            {/* Image Gallery */}
            <section className="col-span-1 md:col-span-7 flex flex-col gap-4">
              <div
                className="group relative aspect-square w-full rounded border border-gray-200 bg-white p-6 flex items-center justify-center cursor-zoom-in"
                onClick={() => setEnlarge(true)}
              >
                <img
                  src={getAllImages()[currentImg]}
                  alt={product.name}
                  className="w-full h-full object-contain mix-blend-multiply"
                />
                <button
                  type="button"
                  className="absolute bottom-4 right-4 rounded border border-gray-300 bg-white p-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-gray-100"
                  onClick={() => setEnlarge(true)}
                  aria-label={t('productDetails.zoomImage')}
                >
                  <span className="text-sm font-semibold text-gray-700">{t('productDetails.zoom')}</span>
                </button>
              </div>

              <div className="grid grid-cols-4 gap-4">
                {getAllImages().slice(0, 4).map((img, idx) => (
                  <button
                    key={img}
                    className={`aspect-square rounded border p-1 flex items-center justify-center transition-colors ${
                      currentImg === idx ? 'border-2 border-primary bg-white' : 'border-gray-300 bg-white hover:border-gray-400'
                    }`}
                    onClick={() => setCurrentImg(idx)}
                    aria-label={t('productDetails.showImage', { index: idx + 1 })}
                  >
                    <img src={img} alt={t('productDetails.thumbnailAlt', { index: idx + 1 })} className="w-full h-full object-contain mix-blend-multiply" />
                  </button>
                ))}
              </div>

              {enlarge && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={() => setEnlarge(false)}>
                  <div className="relative max-w-3xl w-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
                    <img src={getAllImages()[currentImg]} alt={product.name} className="max-h-[80vh] w-auto rounded-2xl shadow-2xl" />
                    <button className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full p-1" onClick={() => setEnlarge(false)} aria-label={t('productDetails.closeEnlargedImage')}>
                      <CloseIcon className="text-black !w-6 !h-6" />
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Product Details */}
            <section className="col-span-1 md:col-span-5 flex flex-col">
              <div className="mb-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-sm bg-gray-200 text-gray-800 text-xs font-semibold uppercase">
                    {productLabel}
                  </span>
                </div>
                <h1 className="mt-1 text-2xl md:text-3xl font-bold text-gray-900">{product.name}</h1>
                {product.price && (
                  <div className="text-3xl font-bold text-primary">{formatPrice(product.price)}</div>
                )}
              </div>

              <hr className="my-4 border-t-2 border-gray-200" />

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-6">
                {product.weight && (
                  <div className="flex flex-col">
                    <span className="text-xs uppercase tracking-wider text-gray-500">{t('productDetails.productWeight')}</span>
                    <span className="mt-1 font-semibold text-gray-900">{product.weight} kg</span>
                  </div>
                )}
              </div>

              <div className="mb-6 rounded border border-gray-300 bg-gray-100 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <label htmlFor="quantity" className="text-sm font-semibold text-gray-700">
                    {t('productDetails.quantity')}
                  </label>
                  <div className="flex items-center overflow-hidden rounded border border-gray-300 bg-white">
                    <button
                      type="button"
                      onClick={decreaseQuantity}
                      className="h-11 w-11 border-r border-gray-300 text-lg text-gray-700 transition-colors hover:bg-gray-100"
                      aria-label={t('productDetails.decreaseQuantity')}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      id="quantity"
                      value={quantity}
                      onChange={handleQuantityChange}
                      min="1"
                      className="h-11 w-12 border-0 bg-transparent text-center text-base font-semibold focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={increaseQuantity}
                      className="h-11 w-11 border-l border-gray-300 text-lg text-gray-700 transition-colors hover:bg-gray-100"
                      aria-label={t('productDetails.increaseQuantity')}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => navigate(`/checkout/${product.id}?quantity=${quantity}`)}
                    className="w-full rounded bg-primary px-4 py-2 text-base font-semibold text-white transition-colors hover:bg-blue-900"
                  >
                    {t('productDetails.checkOutNow')}
                  </button>
                  <button
                    onClick={() => {
                      if (product) {
                        const cartItem = {
                          title: product.name,
                          sku: product.sku,
                          quantity: quantity,
                          price: product.price,
                          weight: product.weight || 0,
                          image: product.image
                        };

                        const success = addToCart(cartItem);
                        if (success) {
                          alert(t('productDetails.addedToCart'));
                        } else {
                          alert(t('productDetails.addToCartFailed'));
                        }
                      }
                    }}
                    className="w-full rounded border-2 border-gray-800 bg-transparent px-4 py-2 text-base font-semibold text-gray-800 transition-colors hover:bg-gray-200"
                  >
                    {t('productDetails.addToCart')}
                  </button>
                </div>

              </div>

              <div className="flex flex-col">
                <h3 className="mb-3 border-b-2 border-gray-200 pb-2 text-xl font-bold text-gray-900">
                  {t('productDetails.description')}
                </h3>
                <p className="whitespace-pre-line text-base leading-relaxed text-gray-700">
                  {product.description}
                </p>
              </div>
            </section>
          </div>

          <div className="mt-8 text-center">
            <button onClick={() => navigate(-1)} className="flex items-center text-primary font-semibold hover:underline mx-auto">
              <ArrowBackIcon className="mr-2 !w-5 !h-5" />
              {t('productDetails.browseOtherTools')}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ProductDetails;